import React, { useState, useEffect } from "react";
import * as XLSX from 'xlsx';
import { buildApiUrl } from '../config';
import "./Reports.css"; // Ensure consistent styling

const Reports = () => {
    const [outstandingCompanies, setOutstandingCompanies] = useState([]);
    const [outstandingSuppliers, setOutstandingSuppliers] = useState([]);
    const [outstandingWalkins, setOutstandingWalkins] = useState([]);
    const [outstandingWorkers, setOutstandingWorkers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        fetchOutstandingBalances();
    }, []);

    const fetchOutstandingBalances = async () => {
        try {
            const response = await fetch(buildApiUrl("api/reports/outstanding"));
            const data = await response.json();

            if (data.success) {
                // ✅ Sort by balance (Descending Order)
                const sortedCompanies = (data.outstandingCompanies || []).sort((a, b) => b.balance - a.balance);
                const sortedSuppliers = (data.outstandingSuppliers || []).sort((a, b) => b.balance - a.balance);
                const sortedWalkins = (data.outstandingWalkins || []).sort((a, b) => b.balance - a.balance);
                const sortedWorkers = (data.outstandingWorkers || []).sort((a, b) => b.balance - a.balance);

                setOutstandingCompanies(sortedCompanies);
                setOutstandingSuppliers(sortedSuppliers);
                setOutstandingWalkins(sortedWalkins);
                setOutstandingWorkers(sortedWorkers);
            } else {
                setError("Failed to fetch data.");
            }
        } catch (err) {
            console.error("Error fetching reports:", err);
            setError("Error fetching reports.");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Calculate Total Outstanding Amount
    const totalCompanyBalance = outstandingCompanies.reduce((acc, company) => acc + company.balance, 0);
    const totalSupplierBalance = outstandingSuppliers.reduce((acc, supplier) => acc + supplier.balance, 0);
    const totalWalkinBalance = outstandingWalkins.reduce((acc, walkin) => acc + walkin.balance, 0);
    const totalWorkerBalance = outstandingWorkers.reduce((acc, worker) => acc + worker.balance, 0);

    // Export Companies to Excel
    const exportCompaniesToExcel = () => {
        const dataToExport = outstandingCompanies.map(company => ({
            'Company Name': company.company_name,
            'Outstanding Balance (AED)': company.balance.toFixed(2)
        }));
        
        dataToExport.push({
            'Company Name': 'Total',
            'Outstanding Balance (AED)': totalCompanyBalance.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Companies Credit");
        
        const fileName = `Companies_Credit_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    // Export Suppliers to Excel
    const exportSuppliersToExcel = () => {
        const dataToExport = outstandingSuppliers.map(supplier => ({
            'Supplier Name': supplier.SupplierName,
            'Outstanding Balance (AED)': supplier.balance.toFixed(2)
        }));
        
        dataToExport.push({
            'Supplier Name': 'Total',
            'Outstanding Balance (AED)': totalSupplierBalance.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Suppliers Debit");
        
        const fileName = `Suppliers_Debit_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    // Export Walk-in Customers to Excel
    const exportWalkinsToExcel = () => {
        const dataToExport = outstandingWalkins.map(walkin => ({
            'Customer Name': walkin.name,
            'Vehicle No': walkin.vehicle_no,
            'Outstanding Balance (AED)': walkin.balance.toFixed(2)
        }));
        
        dataToExport.push({
            'Customer Name': 'Total',
            'Vehicle No': '',
            'Outstanding Balance (AED)': totalWalkinBalance.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Walk-ins Credit");
        
        const fileName = `Walkins_Credit_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    // Export Overtime Workers to Excel
    const exportWorkersToExcel = () => {
        const dataToExport = outstandingWorkers.map(worker => ({
            'Worker Name': worker.WorkerName,
            'Outstanding Balance (AED)': worker.balance.toFixed(2)
        }));
        
        dataToExport.push({
            'Worker Name': 'Total',
            'Outstanding Balance (AED)': totalWorkerBalance.toFixed(2)
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Workers Overtime");
        
        const fileName = `Workers_Overtime_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="error">{error}</p>;

    return (
        <div className="reports-container">
            <h1>Outstanding Balances Report</h1>

            {/* 🔹 Outstanding Companies Table */}
            <div className="table-container1">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h2>Companies Credit Report</h2>
                    <button 
                        onClick={exportCompaniesToExcel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '24px',
                            color: '#217346',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Export to Excel"
                    >
                        📊
                    </button>
                </div>
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Company Name</th>
                            <th>Outstanding Balance (AED)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outstandingCompanies.length > 0 ? (
                            outstandingCompanies.map((company, index) => (
                                <tr key={index}>
                                    <td>{company.company_name}</td>
                                    <td className="balance-amount">{company.balance.toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="2">No outstanding payments.</td>
                            </tr>
                        )}
                        {/* 🔹 Total Outstanding Row */}
                        {outstandingCompanies.length > 0 && (
                            <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td className="total-amount"><strong>{totalCompanyBalance.toFixed(2)}</strong></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* 🔹 Outstanding Suppliers Table */}
            <div className="table-container1">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h2>Suppliers Debit Report</h2>
                    <button 
                        onClick={exportSuppliersToExcel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '24px',
                            color: '#217346',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Export to Excel"
                    >
                        📊
                    </button>
                </div>
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Supplier Name</th>
                            <th>Outstanding Balance (AED)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outstandingSuppliers.length > 0 ? (
                            outstandingSuppliers.map((supplier, index) => (
                                <tr key={index}>
                                    <td>{supplier.SupplierName}</td>
                                    <td className="balance-amount">{supplier.balance.toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="2">No pending payments.</td>
                            </tr>
                        )}
                        {/* 🔹 Total Outstanding Row */}
                        {outstandingSuppliers.length > 0 && (
                            <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td className="total-amount"><strong>{totalSupplierBalance.toFixed(2)}</strong></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* 🔹 Outstanding Walk-in Customers Table */}
            <div className="table-container1">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h2>Walk-in Customers Credit Report</h2>
                    <button 
                        onClick={exportWalkinsToExcel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '24px',
                            color: '#217346',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Export to Excel"
                    >
                        📊
                    </button>
                </div>
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Customer Name</th>
                            <th>Vehicle No</th>
                            <th>Outstanding Balance (AED)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outstandingWalkins.length > 0 ? (
                            outstandingWalkins.map((walkin, index) => (
                                <tr key={index}>
                                    <td>{walkin.name}</td>
                                    <td>{walkin.vehicle_no}</td>
                                    <td className="balance-amount">{walkin.balance.toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3">No outstanding payments.</td>
                            </tr>
                        )}
                        {/* 🔹 Total Outstanding Row */}
                        {outstandingWalkins.length > 0 && (
                            <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td></td>
                                <td className="total-amount"><strong>{totalWalkinBalance.toFixed(2)}</strong></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* 🔹 Outstanding Overtime Workers Table */}
            <div className="table-container1">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <h2>Overtime Workers Outstanding Report</h2>
                    <button 
                        onClick={exportWorkersToExcel}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '24px',
                            color: '#217346',
                            padding: '5px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                        title="Export to Excel"
                    >
                        📊
                    </button>
                </div>
                <table className="styled-table">
                    <thead>
                        <tr>
                            <th>Worker Name</th>
                            <th>Outstanding Balance (AED)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {outstandingWorkers.length > 0 ? (
                            outstandingWorkers.map((worker, index) => (
                                <tr key={index}>
                                    <td>{worker.WorkerName}</td>
                                    <td className="balance-amount">{worker.balance.toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="2">No outstanding overtime payments.</td>
                            </tr>
                        )}
                        {/* 🔹 Total Outstanding Row */}
                        {outstandingWorkers.length > 0 && (
                            <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td className="total-amount"><strong>{totalWorkerBalance.toFixed(2)}</strong></td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Reports;
